import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef
} from '@angular/core';

import {
  CommonModule
} from '@angular/common';

import {
  FormsModule
} from '@angular/forms';

import {
  HttpClient
} from '@angular/common/http';

import {
  RouterLink
} from '@angular/router';

import {
  finalize
} from 'rxjs/operators';

import {
  firstValueFrom
} from 'rxjs';

import {
  LanguageService
} from '../../services/language.service';

import {
  AutoTranslateService
} from '../../services/auto-translate.service';


@Component({
  selector: 'app-admin-categories',

  standalone: true,

  imports: [
    CommonModule,
    FormsModule,
    RouterLink
  ],

  templateUrl: './admin-categories.html',

  styleUrl: './admin-categories.css'
})
export class AdminCategories
  implements OnInit, OnDestroy {

  // =========================
  // CATEGORY DATA
  // =========================

  categories: any[] = [];

  mainCategories: any[] = [];

  subCategories: any[] = [];

  allCategories: any[] = [];

  // =========================
  // SEARCH + FAST TREE
  // =========================

  categorySearch = '';

  popularSearch = '';

  searchType: 'ALL' | 'MAIN' | 'SUB' | 'CHILD' = 'ALL';

  readonly typeFilters: { value: 'ALL' | 'MAIN' | 'SUB' | 'CHILD'; label: string }[] = [
    { value: 'ALL', label: 'সব' },
    { value: 'MAIN', label: 'Main' },
    { value: 'SUB', label: 'Sub' },
    { value: 'CHILD', label: 'Child' }
  ];

  searchResults: any[] = [];

  searchTotal = 0;

  searchLimit = 60;

  popularVisible: any[] = [];

  expandedIds = new Set<number>();

  highlightId: number | null = null;

  stats = { main: 0, sub: 0, child: 0 };

  private searchTimer: any = null;

  private popularTimer: any = null;

  // =========================
  // EDIT CONTROL
  // =========================

  editingId: number | null = null;

  selectedFile: File | null = null;

  previewImage: string | null = null;


  // =========================
  // BULK IMPORT (taxonomy Excel)
  // =========================

  importFile: File | null = null;

  importing = false;

  // 🖼️ ক্যাটাগরি ছবি বাল্ক ইম্পোর্ট
  imageFiles: File[] = [];
  imageOverwrite = false;
  imageApplyAllSameName = true;
  imageImporting = false;
  imageProgress = '';
  imageResult: any = null;

  // 🕘 ছবি Import ইতিহাস (Undo / Redo / ছবি বদল)
  imgBatches: any[] = [];
  imgTrash: any[] = [];
  openBatchId: string | null = null;
  openBatchItems: any[] = [];
  openBatchTotal = 0;
  imgBusy = '';
  imgMsg = '';

  importResult: {
    mainCreated: number;
    subCreated: number;
    childCreated: number;
    skippedExisting: number;
    errors: string[];
    batchId: string | null;
  } | null = null;


  // =========================
  // LOADING CONTROL
  // =========================

  loading = false;

  saving = false;

  popularSavingId: number | null = null;


  // =========================
  // FORM
  // =========================

  form = {

    name: '',

    nameBn: '',

    type: 'MAIN',

    parentId: null as number | null
  };


  // =========================
  // API
  // =========================

  readonly api =
    'https://superbangladesh-api-1.onrender.com/api/categories';

  readonly backendBase =
    'https://superbangladesh-api-1.onrender.com';


  // =========================
  // CONSTRUCTOR
  // =========================

  constructor(
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    public languageService: LanguageService,
    private autoTranslate: AutoTranslateService
  ) {}

  t(key: string): string {
    return this.languageService.translate(key);
  }

  translatingCategoryName = false;

  // ✅ ইংরেজি লেখার পর বাংলা ফাঁকা থাকলে অটো ট্রান্সলেট
  autoFillCategoryNameBn(): void {

    if (!this.form.name?.trim()) return;
    if (this.form.nameBn?.trim()) return;

    this.translatingCategoryName = true;

    this.autoTranslate
      .translate(this.form.name, 'bn')
      .subscribe(bn => {

        this.translatingCategoryName = false;

        if (bn) {
          this.form.nameBn = bn;
        }
      });
  }

  // ✅ বাংলা লেখার পর ইংরেজি ফাঁকা থাকলে অটো ট্রান্সলেট
  autoFillCategoryNameEn(): void {

    if (!this.form.nameBn?.trim()) return;
    if (this.form.name?.trim()) return;

    this.translatingCategoryName = true;

    this.autoTranslate
      .translate(this.form.nameBn, 'en')
      .subscribe(en => {

        this.translatingCategoryName = false;

        if (en) {
          this.form.name = en;
        }
      });
  }


  // =========================
  // INIT
  // =========================

  ngOnInit(): void {

    this.load();

    this.loadTrash();

    this.loadImportBatches();

    this.loadImageBatches();
  }

  ngOnDestroy(): void {

    if (this.undoTimer) {
      clearTimeout(this.undoTimer);
    }
  }


  // =========================
  // LOAD CATEGORY TREE
  // =========================

  load(): void {

    // IMPORTANT:
    // পুরনো "if (this.loading) return"
    // বাদ দেওয়া হয়েছে

    this.loading = true;

    this.cdr.detectChanges();


    this.http
      .get<any[]>(
        `${this.api}/tree`
      )
      .subscribe({

        next: (res: any[]) => {

          const data =
            Array.isArray(res)
              ? res
              : [];


          const categories: any[] = [];

          const mainCategories: any[] = [];

          const subCategories: any[] = [];

          const allCategories: any[] = [];


          // =========================
          // BUILD ALL ARRAYS
          // =========================

          for (const main of data) {

            this.normalizeCategory(main);

            main.children =
              Array.isArray(main?.children)
                ? main.children
                : [];


            categories.push(main);

            mainCategories.push(main);

            allCategories.push(main);


            // =========================
            // SUB CATEGORIES
            // =========================

            for (const sub of main.children) {

              this.normalizeCategory(sub);

              sub.children =
                Array.isArray(sub?.children)
                  ? sub.children
                  : [];


              subCategories.push(sub);

              allCategories.push(sub);


              // =========================
              // CHILD CATEGORIES
              // =========================

              for (const child of sub.children) {

                this.normalizeCategory(child);

                child.children =
                  Array.isArray(child?.children)
                    ? child.children
                    : [];


                allCategories.push(child);
              }
            }
          }


          // =========================
          // SORT POPULAR FIRST
          // =========================

          allCategories.sort(
            (a: any, b: any) => {

              const aPopular =
                Boolean(a?.popular);

              const bPopular =
                Boolean(b?.popular);


              if (
                aPopular &&
                !bPopular
              ) {

                return -1;
              }


              if (
                !aPopular &&
                bPopular
              ) {

                return 1;
              }


              if (
                aPopular &&
                bPopular
              ) {

                const aOrder =
                  Number(
                    a?.popularOrder
                  ) || 0;

                const bOrder =
                  Number(
                    b?.popularOrder
                  ) || 0;


                if (
                  aOrder !== bOrder
                ) {

                  return (
                    aOrder - bOrder
                  );
                }
              }


              return (
                Number(a?.id || 0) -
                Number(b?.id || 0)
              );
            }
          );


          // =========================
          // NEW ARRAY REFERENCES
          // =========================

          this.categories = [
            ...categories
          ];

          this.mainCategories = [
            ...mainCategories
          ];

          this.subCategories = [
            ...subCategories
          ];

          this.allCategories = [
            ...allCategories
          ];

          // একবারই সার্চ-ইনডেক্স বানানো হয় (প্রতি কীস্ট্রোকে নয়)
          this.buildIndex();

          this.runSearch();

          this.rebuildPopular();


          // =========================
          // STOP LOADING
          // =========================

          this.loading = false;


          // =========================
          // FORCE UI UPDATE
          // =========================

          this.cdr.detectChanges();


        },


        error: (err: any) => {

          console.error(
            'Category tree load failed',
            err
          );


          this.categories = [];

          this.mainCategories = [];

          this.subCategories = [];

          this.allCategories = [];


          this.loading = false;


          // FORCE ERROR UI UPDATE

          this.cdr.detectChanges();


          alert(
            this.getErrorMessage(
              err,
              'Categories could not be loaded'
            )
          );
        }

      });
  }


  // =========================
  // NORMALIZE CATEGORY
  // =========================

 private normalizeCategory(
  category: any
): void {

  if (!category) {
    return;
  }

  category.popular =
    Boolean(category.popular);

  const order =
    Number(category.popularOrder);

  category.popularOrder =
    Number.isFinite(order)
      ? order
      : 0;

  const displayOrder =
    Number(category.displayOrder);

  category.displayOrder =
    Number.isFinite(displayOrder)
      ? displayOrder
      : 0;
}

  // =========================
  // SORT POPULAR FIRST
  // =========================

  sortPopularCategories(): void {

    const sorted = [
      ...this.allCategories
    ];


    sorted.sort(
      (a: any, b: any) => {

        const aPopular =
          Boolean(a?.popular);

        const bPopular =
          Boolean(b?.popular);


        if (
          aPopular &&
          !bPopular
        ) {

          return -1;
        }


        if (
          !aPopular &&
          bPopular
        ) {

          return 1;
        }


        if (
          aPopular &&
          bPopular
        ) {

          const aOrder =
            Number(
              a?.popularOrder
            ) || 0;

          const bOrder =
            Number(
              b?.popularOrder
            ) || 0;


          if (
            aOrder !== bOrder
          ) {

            return (
              aOrder - bOrder
            );
          }
        }


        return (
          Number(a?.id || 0) -
          Number(b?.id || 0)
        );
      }
    );


    this.allCategories = sorted;


    this.cdr.detectChanges();
  }


  // =========================
  // IMAGE URL FIX
  // CLOUDINARY + LOCAL IMAGE
  // =========================

  getImageUrl(
    imageUrl: string | null | undefined
  ): string {

    if (!imageUrl) {

      return '';
    }


    const url =
      String(imageUrl).trim();


    if (!url) {

      return '';
    }


    // CLOUDINARY OR FULL URL

    if (
      url.startsWith('https://') ||
      url.startsWith('http://')
    ) {

      return url;
    }


    // PROTOCOL RELATIVE

    if (
      url.startsWith('//')
    ) {

      return `https:${url}`;
    }


    // BACKEND RELATIVE URL

    if (
      url.startsWith('/')
    ) {

      return (
        this.backendBase +
        url
      );
    }


    // SIMPLE RELATIVE PATH

    return (
      this.backendBase +
      '/' +
      url
    );
  }


  // =========================
  // IMAGE ERROR
  // =========================

  onImageError(
    event: Event
  ): void {

    const img =
      event.target as HTMLImageElement;


    if (!img) {

      return;
    }


    img.style.display =
      'none';
  }


  // =========================
  // SELECT IMAGE
  // =========================

  onFileSelected(
    event: Event
  ): void {

    const input =
      event.target as HTMLInputElement;


    const file =
      input.files?.[0];


    if (!file) {

      return;
    }


    this.selectedFile = file;


    const reader =
      new FileReader();


    reader.onload = () => {

      this.previewImage =
        reader.result as string;


      this.cdr.detectChanges();
    };


    reader.readAsDataURL(file);
  }


  // =========================
  // SAVE CATEGORY
  // =========================

  save(): void {

    if (this.saving) {

      return;
    }


    const cleanName =
      String(
        this.form.name || ''
      ).trim();


    if (!cleanName) {

      alert(
        'Category name is required'
      );

      return;
    }


    if (
      this.form.type !== 'MAIN' &&
      !this.form.parentId
    ) {

      alert(
        'Please select parent category'
      );

      return;
    }


    const fd =
      new FormData();


    fd.append(
      'name',
      cleanName
    );


    fd.append(
      'nameBn',
      String(
        this.form.nameBn || ''
      ).trim()
    );


    if (
      this.form.type !== 'MAIN' &&
      this.form.parentId !== null
    ) {

      fd.append(
        'parentId',
        String(
          this.form.parentId
        )
      );
    }


    if (this.selectedFile) {

      fd.append(
        'file',
        this.selectedFile
      );
    }


    this.saving = true;

    this.cdr.detectChanges();


    // =========================
    // UPDATE CATEGORY
    // =========================

    if (
      this.editingId !== null
    ) {

      this.http
        .put<any>(
          `${this.api}/${this.editingId}`,
          fd
        )
        .pipe(

          finalize(() => {

            this.saving = false;

            this.cdr.detectChanges();
          })

        )
        .subscribe({

          next: () => {

            alert(
              'Category Updated'
            );

            this.reset();
          },


          error: (err: any) => {

            console.error(
              'Category update failed',
              err
            );


            alert(
              this.getErrorMessage(
                err,
                'Category update failed'
              )
            );
          }

        });


      return;
    }


    // =========================
    // ADD CATEGORY
    // =========================

    this.http
      .post<any>(
        this.api,
        fd
      )
      .pipe(

        finalize(() => {

          this.saving = false;

          this.cdr.detectChanges();
        })

      )
      .subscribe({

        next: () => {

          alert(
            'Category Added'
          );

          this.reset();
        },


        error: (err: any) => {

          console.error(
            'Category add failed',
            err
          );


          alert(
            this.getErrorMessage(
              err,
              'Category add failed'
            )
          );
        }

      });
  }


  // =========================
  // EDIT CATEGORY
  // =========================

  edit(c: any): void {

    if (!c) {

      return;
    }


    this.editingId =
      Number(c.id);


    let type = 'MAIN';

    if (c._level === 3) {

      type = 'CHILD';

    } else if (c._level === 2) {

      type = 'SUB';
    }


    this.form = {

      name:
        c.name || '',

      nameBn:
        c.nameBn || '',

      type,

      parentId:
        c._parentId ?? c.parent?.id ?? null
    };


    this.previewImage =
      this.getImageUrl(
        c.imageUrl
      );


    this.selectedFile = null;


    this.cdr.detectChanges();


    window.scrollTo({

      top: 0,

      behavior: 'smooth'

    });
  }


  // =========================================================
  // 🗑️ DELETE CATEGORY → TRASH (Undo / Restore করা যায়)
  //
  // ক্যাটাগরি + তার সব সাব/চাইল্ড একসাথে ট্র্যাশে যায়।
  // ডিলিটের পর ১০ সেকেন্ড "Undo" বাটন দেখায়; পরেও নিচের Trash প্যানেল
  // থেকে ৩০ দিন পর্যন্ত ফেরানো যায়।
  // =========================================================

  // Undo টোস্ট
  undoToast: { message: string; groupId: string } | null = null;

  private undoTimer: any = null;

  // বাল্ক সিলেকশন (checkbox)
  selectedIds = new Set<number>();

  deleting = false;

  private findCategoryName(id: number): string {

    const c = this.allCategories.find(
      (x: any) => Number(x.id) === Number(id)
    );

    return c?.name || 'Category';
  }

  private showUndo(message: string, groupId: string): void {

    if (this.undoTimer) {
      clearTimeout(this.undoTimer);
    }

    this.undoToast = { message, groupId };

    this.undoTimer = setTimeout(() => {

      this.undoToast = null;

      this.cdr.detectChanges();

    }, 10000);

    this.cdr.detectChanges();
  }

  dismissUndo(): void {

    if (this.undoTimer) {
      clearTimeout(this.undoTimer);
    }

    this.undoToast = null;

    this.cdr.detectChanges();
  }

  delete(id: number): void {

    if (this.deleting) {
      return;
    }

    const name = this.findCategoryName(id);

    if (
      !confirm(
        `"${name}" এবং এর ভেতরের সব সাব-ক্যাটাগরি ট্র্যাশে পাঠাবেন?\n\n` +
        `(৩০ দিনের মধ্যে ট্র্যাশ থেকে ফিরিয়ে আনা যাবে)`
      )
    ) {
      return;
    }

    this.deleting = true;

    this.http
      .delete<any>(`${this.api}/${id}`)
      .pipe(
        finalize(() => {
          this.deleting = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({

        next: (res: any) => {

          this.afterTrashed(res, `"${name}"`);
        },

        error: (err: any) => {

          console.error('Category delete failed', err);

          alert(
            this.getErrorMessage(
              err,
              'ক্যাটাগরি ডিলিট করা যায়নি'
            )
          );
        }
      });
  }

  // ডিলিট/বাল্ক-ডিলিট/Undo-Import — তিনটার পরের কাজ একই
  private afterTrashed(res: any, label: string): void {

    const count = Number(res?.deletedCount) || 0;

    const blocked: string[] =
      Array.isArray(res?.blocked) ? res.blocked : [];

    if (count > 0 && res?.groupId) {

      this.showUndo(
        `${label} সহ মোট ${count}টি ক্যাটাগরি ট্র্যাশে গেছে`,
        res.groupId
      );
    }

    if (blocked.length) {

      alert(
        'কিছু ক্যাটাগরি ডিলিট করা যায়নি:\n\n' +
        blocked.join('\n\n')
      );
    }

    this.selectedIds.clear();

    this.load();

    this.loadTrash();

    this.loadImportBatches();
  }

  // ↩️ Undo (গ্রুপ ধরে — একটা ডিলিটে যা যা গেছে সব ফেরে)
  undoDelete(): void {

    const toast = this.undoToast;

    if (!toast) {
      return;
    }

    this.dismissUndo();

    this.http
      .post<any>(
        `${this.api}/restore-group/${toast.groupId}`,
        null
      )
      .subscribe({

        next: (res: any) => {

          alert(`${res?.restoredCount ?? 0}টি ক্যাটাগরি ফিরিয়ে আনা হয়েছে`);

          this.load();

          this.loadTrash();

          this.loadImportBatches();
        },

        error: (err: any) => {

          alert(
            this.getErrorMessage(err, 'Undo করা যায়নি')
          );
        }
      });
  }


  // =========================
  // BULK SELECT + DELETE
  // =========================

  isSelected(id: number): boolean {

    return this.selectedIds.has(Number(id));
  }

  toggleSelect(id: number): void {

    const n = Number(id);

    if (this.selectedIds.has(n)) {
      this.selectedIds.delete(n);
    } else {
      this.selectedIds.add(n);
    }

    this.cdr.detectChanges();
  }

  get selectedCount(): number {

    return this.selectedIds.size;
  }

  get allMainSelected(): boolean {

    const mains = this.filteredCategories;

    return (
      mains.length > 0 &&
      mains.every((m: any) => this.selectedIds.has(Number(m.id)))
    );
  }

  toggleSelectAllMain(): void {

    const mains = this.filteredCategories;

    if (this.allMainSelected) {

      mains.forEach((m: any) => this.selectedIds.delete(Number(m.id)));

    } else {

      mains.forEach((m: any) => this.selectedIds.add(Number(m.id)));
    }

    this.cdr.detectChanges();
  }

  clearSelection(): void {

    this.selectedIds.clear();

    this.cdr.detectChanges();
  }

  bulkDelete(): void {

    if (!this.selectedIds.size || this.deleting) {
      return;
    }

    if (
      !confirm(
        `বাছাই করা ${this.selectedIds.size}টি ক্যাটাগরি (এবং এদের সব সাব-ক্যাটাগরি) ` +
        `ট্র্যাশে পাঠাবেন?\n\n(৩০ দিনের মধ্যে ফিরিয়ে আনা যাবে)`
      )
    ) {
      return;
    }

    this.deleting = true;

    this.http
      .post<any>(
        `${this.api}/bulk-delete`,
        { ids: Array.from(this.selectedIds) }
      )
      .pipe(
        finalize(() => {
          this.deleting = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({

        next: (res: any) => {

          this.afterTrashed(res, 'বাছাই করা ক্যাটাগরি');
        },

        error: (err: any) => {

          console.error('Bulk delete failed', err);

          alert(
            this.getErrorMessage(err, 'বাল্ক ডিলিট করা যায়নি')
          );
        }
      });
  }


  // =========================================================
  // 🗑️ TRASH PANEL
  // =========================================================

  readonly RETENTION_DAYS = 30;

  trashOpen = false;

  trashLoading = false;

  trashItems: any[] = [];

  trashGroups: {
    groupId: string;
    root: any;
    items: any[];
    deletedAt: Date | null;
    daysLeft: number;
    expanded: boolean;
  }[] = [];

  trashBusy = false;

  // 🗑️ উপরের "Trash" বাটন: ট্র্যাশ প্যানেল খুলে সেখানে স্ক্রল করে
  goToTrash(): void {

    this.trashOpen = true;

    this.loadTrash();

    this.cdr.detectChanges();

    setTimeout(() => {
      document
        .getElementById('category-trash-panel')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  }

  toggleTrash(): void {

    this.trashOpen = !this.trashOpen;

    if (this.trashOpen) {
      this.loadTrash();
    }
  }

  private parseDate(value: any): Date | null {

    if (!value) {
      return null;
    }

    // Jackson কখনো [y,m,d,h,mi,s,nano] অ্যারে দেয়
    if (Array.isArray(value)) {

      const [y, mo, d, h = 0, mi = 0, sec = 0] = value;

      return new Date(y, mo - 1, d, h, mi, sec);
    }

    const dt = new Date(value);

    return isNaN(dt.getTime()) ? null : dt;
  }

  loadTrash(): void {

    this.trashLoading = true;

    this.http
      .get<any[]>(`${this.api}/admin-trash`)
      .pipe(
        finalize(() => {
          this.trashLoading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({

        next: (items: any[]) => {

          const list = Array.isArray(items) ? items : [];

          this.trashItems = list;

          const previouslyExpanded = new Set(
            this.trashGroups
              .filter(g => g.expanded)
              .map(g => g.groupId)
          );

          const byGroup = new Map<string, any[]>();

          for (const it of list) {

            const key = it.groupId || `single-${it.id}`;

            if (!byGroup.has(key)) {
              byGroup.set(key, []);
            }

            byGroup.get(key)!.push(it);
          }

          const groups: any[] = [];

          byGroup.forEach((members, groupId) => {

            // উপরের লেভেলের ক্যাটাগরিটাই গ্রুপের "নাম"
            const sorted = [...members].sort(
              (a, b) => (a.level - b.level) || (a.id - b.id)
            );

            const deletedAt = this.parseDate(sorted[0].deletedAt);

            const ageDays = deletedAt
              ? Math.floor((Date.now() - deletedAt.getTime()) / 86400000)
              : 0;

            groups.push({
              groupId,
              root: sorted[0],
              items: sorted,
              deletedAt,
              daysLeft: Math.max(0, this.RETENTION_DAYS - ageDays),
              expanded: previouslyExpanded.has(groupId)
            });
          });

          // নতুন ডিলিট আগে
          groups.sort(
            (a, b) =>
              (b.deletedAt?.getTime() || 0) - (a.deletedAt?.getTime() || 0)
          );

          this.trashGroups = groups;
        },

        error: (err: any) => {

          console.error('Trash load failed', err);

          this.trashItems = [];

          this.trashGroups = [];
        }
      });
  }

  restoreGroup(g: any): void {

    if (this.trashBusy) {
      return;
    }

    this.trashBusy = true;

    const url = g.groupId.startsWith('single-')
      ? `${this.api}/${g.root.id}/restore`
      : `${this.api}/restore-group/${g.groupId}`;

    this.http
      .post<any>(url, null)
      .pipe(finalize(() => {
        this.trashBusy = false;
        this.cdr.detectChanges();
      }))
      .subscribe({

        next: (res: any) => {

          alert(`${res?.restoredCount ?? 0}টি ক্যাটাগরি ফিরিয়ে আনা হয়েছে`);

          this.load();

          this.loadTrash();

          this.loadImportBatches();
        },

        error: (err: any) => {

          alert(this.getErrorMessage(err, 'Restore করা যায়নি'));
        }
      });
  }

  // গ্রুপের ভেতর থেকে শুধু একটা ক্যাটাগরি ফেরানো (সাথে তার ট্র্যাশে থাকা parent-ও ফেরে)
  restoreItem(item: any): void {

    if (this.trashBusy) {
      return;
    }

    this.trashBusy = true;

    this.http
      .post<any>(`${this.api}/${item.id}/restore`, null)
      .pipe(finalize(() => {
        this.trashBusy = false;
        this.cdr.detectChanges();
      }))
      .subscribe({

        next: (res: any) => {

          alert(`${res?.restoredCount ?? 0}টি ক্যাটাগরি ফিরিয়ে আনা হয়েছে`);

          this.load();

          this.loadTrash();
        },

        error: (err: any) => {

          alert(this.getErrorMessage(err, 'Restore করা যায়নি'));
        }
      });
  }

  restoreAllTrash(): void {

    if (this.trashBusy || !this.trashItems.length) {
      return;
    }

    if (!confirm(`ট্র্যাশের সব ${this.trashItems.length}টি ক্যাটাগরি ফিরিয়ে আনবেন?`)) {
      return;
    }

    this.trashBusy = true;

    this.http
      .post<any>(`${this.api}/restore-all`, null)
      .pipe(finalize(() => {
        this.trashBusy = false;
        this.cdr.detectChanges();
      }))
      .subscribe({

        next: (res: any) => {

          alert(`${res?.restoredCount ?? 0}টি ক্যাটাগরি ফিরিয়ে আনা হয়েছে`);

          this.load();

          this.loadTrash();

          this.loadImportBatches();
        },

        error: (err: any) => {

          alert(this.getErrorMessage(err, 'Restore করা যায়নি'));
        }
      });
  }

  permanentDeleteGroup(g: any): void {

    if (this.trashBusy) {
      return;
    }

    if (
      !confirm(
        `"${g.root.name}" (${g.items.length}টি ক্যাটাগরি) চিরতরে মুছে ফেলবেন?\n\n` +
        `⚠️ এরপর আর ফেরানো যাবে না।`
      )
    ) {
      return;
    }

    const url = g.groupId.startsWith('single-')
      ? `${this.api}/${g.root.id}/permanent`
      : `${this.api}/permanent-group/${g.groupId}`;

    this.runPurge(url);
  }

  emptyTrash(): void {

    if (this.trashBusy || !this.trashItems.length) {
      return;
    }

    if (
      !confirm(
        `ট্র্যাশের সব ${this.trashItems.length}টি ক্যাটাগরি চিরতরে মুছে ফেলবেন?\n\n` +
        `⚠️ এরপর আর ফেরানো যাবে না।`
      )
    ) {
      return;
    }

    this.runPurge(`${this.api}/admin-trash/empty`);
  }

  private runPurge(url: string): void {

    this.trashBusy = true;

    this.http
      .delete<any>(url)
      .pipe(finalize(() => {
        this.trashBusy = false;
        this.cdr.detectChanges();
      }))
      .subscribe({

        next: (res: any) => {

          const blocked: string[] =
            Array.isArray(res?.blocked) ? res.blocked : [];

          alert(
            `${res?.deletedCount ?? 0}টি ক্যাটাগরি চিরতরে মোছা হয়েছে` +
            (blocked.length ? '\n\n⚠️ ' + blocked.join('\n') : '')
          );

          this.loadTrash();
        },

        error: (err: any) => {

          alert(
            this.getErrorMessage(err, 'স্থায়ীভাবে মোছা যায়নি')
          );
        }
      });
  }


  // =========================================================
  // 📥 IMPORT HISTORY + UNDO IMPORT
  // =========================================================

  importBatches: {
    batchId: string;
    importedAt: any;
    activeCount: number;
  }[] = [];

  undoingBatchId: string | null = null;

  loadImportBatches(): void {

    this.http
      .get<any[]>(`${this.api}/import-batches`)
      .subscribe({

        next: (list: any[]) => {

          this.importBatches = Array.isArray(list) ? list : [];

          this.cdr.detectChanges();
        },

        error: () => {

          this.importBatches = [];
        }
      });
  }

  formatDateTime(value: any): string {

    const d = this.parseDate(value);

    return d
      ? d.toLocaleString('bn-BD', { dateStyle: 'medium', timeStyle: 'short' })
      : '';
  }

  undoImport(batchId: string | null, count?: number): void {

    if (!batchId || this.undoingBatchId) {
      return;
    }

    if (
      !confirm(
        `এই Import থেকে তৈরি হওয়া ${count ?? ''} ক্যাটাগরি ট্র্যাশে পাঠাবেন?\n\n` +
        `(যেসব ক্যাটাগরিতে প্রোডাক্ট আছে সেগুলো বাদ যাবে। ৩০ দিনের মধ্যে ফেরানো যাবে)`
      )
    ) {
      return;
    }

    this.undoingBatchId = batchId;

    this.http
      .post<any>(
        `${this.api}/import-batches/${batchId}/undo`,
        null
      )
      .pipe(finalize(() => {
        this.undoingBatchId = null;
        this.cdr.detectChanges();
      }))
      .subscribe({

        next: (res: any) => {

          if (this.importResult?.batchId === batchId) {
            this.importResult = null;
          }

          this.afterTrashed(res, 'Import-এর ক্যাটাগরি');
        },

        error: (err: any) => {

          alert(
            this.getErrorMessage(err, 'Import Undo করা যায়নি')
          );
        }
      });
  }


  // =========================
  // TOGGLE POPULAR
  // =========================

  togglePopular(
    category: any
  ): void {

    if (!category) {

      return;
    }


    if (
      this.popularSavingId !== null
    ) {

      return;
    }


    const newPopular =
      !Boolean(
        category.popular
      );


    let newOrder = 0;


    if (newPopular) {

      const currentOrder =
        Number(
          category.popularOrder
        );


      newOrder =
        Number.isFinite(
          currentOrder
        ) &&
        currentOrder > 0

          ? Math.floor(
              currentOrder
            )

          : this.getNextPopularOrder();
    }


    this.updatePopular(
      category,
      newPopular,
      newOrder
    );
  }


  // =========================
  // SAVE POPULAR ORDER
  // =========================

  savePopularOrder(
    category: any
  ): void {

    if (!category) {

      return;
    }


    if (
      !Boolean(
        category.popular
      )
    ) {

      alert(
        'First enable this category as Popular'
      );

      return;
    }


    if (
      this.popularSavingId !== null
    ) {

      return;
    }


    let order =
      Number(
        category.popularOrder
      );


    if (
      !Number.isFinite(order) ||
      order < 1
    ) {

      order = 1;
    }


    order =
      Math.floor(order);


    category.popularOrder =
      order;


    this.updatePopular(
      category,
      true,
      order
    );
  }

  saveDisplayOrder(
  category: any
): void {

  if (!category) {
    return;
  }

  let order =
    Number(category.displayOrder);

  if (
    !Number.isFinite(order) ||
    order < 0
  ) {
    order = 0;
  }

  category.displayOrder =
    Math.floor(order);

  this.http
    .put(
      `${this.api}/${category.id}/display-order?order=${category.displayOrder}`,
      null
    )
    .subscribe({

      next: () => {

        alert('Display Order Updated');

        this.load();

      },

      error: (err) => {

        alert(
          this.getErrorMessage(
            err,
            'Display Order Update Failed'
          )
        );

      }

    });

}


  // =========================
  // UPDATE POPULAR
  // =========================

  private updatePopular(
    category: any,
    popular: boolean,
    popularOrder: number
  ): void {

    this.popularSavingId =
      Number(
        category.id
      );


    this.cdr.detectChanges();


    const safeOrder =
      Number.isFinite(
        Number(popularOrder)
      )

        ? Math.max(
            0,
            Math.floor(
              Number(popularOrder)
            )
          )

        : 0;


    const url =
      `${this.api}/${category.id}/popular` +
      `?popular=${encodeURIComponent(
        String(popular)
      )}` +
      `&order=${encodeURIComponent(
        String(safeOrder)
      )}`;


    this.http
      .put<any>(
        url,
        null
      )
      .pipe(

        finalize(() => {

          this.popularSavingId = null;

          this.cdr.detectChanges();
        })

      )
      .subscribe({

        next: (updated: any) => {

          category.popular =
            updated?.popular ??
            popular;


          category.popularOrder =
            updated?.popularOrder ??
            safeOrder;


          // NEW ARRAY REFERENCE

          this.allCategories = [
            ...this.allCategories
          ];


          // FORCE IMMEDIATE UI UPDATE

          this.cdr.detectChanges();


          // RELOAD FRESH SERVER DATA

          this.load();
        },


        error: (err: any) => {

          console.error(
            'Popular category update failed',
            err
          );


          alert(
            this.getErrorMessage(
              err,
              'Popular category update failed'
            )
          );


          this.load();
        }

      });
  }


  // =========================
  // NEXT POPULAR ORDER
  // =========================

  private getNextPopularOrder(): number {

    const orders =
      this.allCategories

        .filter(
          (c: any) =>
            Boolean(
              c?.popular
            )
        )

        .map(
          (c: any) =>
            Number(
              c?.popularOrder
            ) || 0
        )

        .filter(
          (order: number) =>
            order > 0
        );


    if (!orders.length) {

      return 1;
    }


    return (
      Math.max(...orders) + 1
    );
  }


  // =========================
  // ERROR MESSAGE
  // =========================

  private getErrorMessage(
    err: any,
    fallback: string
  ): string {

    if (
      typeof err?.error === 'string' &&
      err.error.trim()
    ) {

      return err.error;
    }


    if (
      typeof err?.error?.message === 'string' &&
      err.error.message.trim()
    ) {

      return err.error.message;
    }


    if (
      typeof err?.message === 'string' &&
      err.message.trim()
    ) {

      return err.message;
    }


    return fallback;
  }


  // =========================
  // RESET FORM
  // =========================

  // =========================================================
  // 🔍 FAST SEARCH (Main + Sub + Child, English + বাংলা)
  // =========================================================

  // select-all (Main) এখনও আগের নামেই কাজ করে
  get filteredCategories() {

    return this.categories;
  }

  get isSearching(): boolean {

    return this.categorySearch.trim().length > 0;
  }

  private norm(s: any): string {

    return String(s ?? '')
      .normalize('NFC')
      .toLowerCase()
      .trim();
  }

  trackById(_i: number, c: any): number {

    return c.id;
  }

  levelLabel(level: number): string {

    if (level === 3) return this.t('childCategory');
    if (level === 2) return this.t('subCategory');
    return this.t('mainCategory');
  }

  // প্রতিটা ক্যাটাগরিতে level / path / search-key বসানো
  private buildIndex(): void {

    const stats = { main: 0, sub: 0, child: 0 };

    const decorate = (
      c: any,
      level: number,
      parent: any,
      main: any
    ) => {

      c._level = level;

      c._parentId = parent ? parent.id : null;

      c._mainId = main ? main.id : c.id;

      c._path =
        level === 1
          ? ''
          : level === 2
            ? main.name
            : `${main.name} › ${parent.name}`;

      c._pathBn =
        level === 1
          ? ''
          : level === 2
            ? (main.nameBn || main.name)
            : `${main.nameBn || main.name} › ${parent.nameBn || parent.name}`;

      c._key = this.norm(`${c.name} ${c.nameBn || ''}`);

      c._pathKey = this.norm(`${c._path} ${c._pathBn}`);
    };

    for (const main of this.categories) {

      decorate(main, 1, null, null);

      stats.main++;

      for (const sub of main.children) {

        decorate(sub, 2, main, main);

        stats.sub++;

        for (const child of sub.children) {

          decorate(child, 3, sub, main);

          stats.child++;
        }
      }
    }

    this.stats = stats;
  }

  onSearchInput(): void {

    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }

    this.searchTimer = setTimeout(() => {

      this.searchLimit = 60;

      this.runSearch();

      this.cdr.detectChanges();

    }, 250);
  }

  setSearchType(type: 'ALL' | 'MAIN' | 'SUB' | 'CHILD'): void {

    this.searchType = type;

    this.searchLimit = 60;

    this.runSearch();

    this.cdr.detectChanges();
  }

  clearSearch(): void {

    this.categorySearch = '';

    this.searchResults = [];

    this.searchTotal = 0;

    this.cdr.detectChanges();
  }

  showMoreResults(): void {

    this.searchLimit += 60;

    this.runSearch();

    this.cdr.detectChanges();
  }

  private levelOf(type: string): number {

    return type === 'MAIN' ? 1 : type === 'SUB' ? 2 : type === 'CHILD' ? 3 : 0;
  }

  private matchScore(c: any, q: string, tokens: string[]): number {

    // সব শব্দ নাম/বাংলা নাম/পাথ-এর কোথাও থাকতে হবে,
    // আর অন্তত একটা শব্দ ক্যাটাগরির নিজের নামে থাকতে হবে
    const hay = c._key + ' ' + c._pathKey;

    let inOwnName = false;

    for (const t of tokens) {

      if (!hay.includes(t)) {
        return -1;
      }

      if (c._key.includes(t)) {
        inOwnName = true;
      }
    }

    if (!inOwnName) {
      return -1;
    }

    const name = this.norm(c.name);

    const nameBn = this.norm(c.nameBn);

    if (name === q || nameBn === q) return 0;

    if (name.startsWith(q) || nameBn.startsWith(q)) return 1;

    if (
      name.includes(' ' + q) ||
      nameBn.includes(' ' + q)
    ) return 2;

    if (c._key.includes(q)) return 3;

    return 4;
  }

  runSearch(): void {

    const q = this.norm(this.categorySearch);

    if (!q) {

      this.searchResults = [];

      this.searchTotal = 0;

      return;
    }

    const tokens = q.split(/\s+/).filter(Boolean);

    const wantLevel = this.levelOf(this.searchType);

    const hits: { c: any; score: number }[] = [];

    for (const c of this.allCategories) {

      if (wantLevel && c._level !== wantLevel) {
        continue;
      }

      const score = this.matchScore(c, q, tokens);

      if (score >= 0) {
        hits.push({ c, score });
      }
    }

    hits.sort(
      (a, b) =>
        (a.score - b.score) ||
        (a.c._level - b.c._level) ||
        (String(a.c.name).length - String(b.c.name).length)
    );

    this.searchTotal = hits.length;

    this.searchResults = hits
      .slice(0, this.searchLimit)
      .map(h => h.c);
  }


  // =========================================================
  // 🌳 COLLAPSIBLE TREE (শুধু খোলা অংশই আঁকা হয়)
  // =========================================================

  isExpanded(id: number): boolean {

    return this.expandedIds.has(Number(id));
  }

  toggleExpand(id: number): void {

    const n = Number(id);

    if (this.expandedIds.has(n)) {
      this.expandedIds.delete(n);
    } else {
      this.expandedIds.add(n);
    }
  }

  collapseAll(): void {

    this.expandedIds.clear();
  }

  // সার্চ রেজাল্ট থেকে ট্রি-তে ঠিক ওই জায়গায় গিয়ে খুলে দেখানো
  revealInTree(c: any): void {

    if (!c) {
      return;
    }

    if (c._level >= 2) {
      this.expandedIds.add(Number(c._mainId));
    }

    if (c._level === 3) {
      this.expandedIds.add(Number(c._parentId));
    }

    this.clearSearch();

    this.highlightId = Number(c.id);

    this.cdr.detectChanges();

    setTimeout(() => {

      document
        .getElementById('cat-' + c.id)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });

    }, 60);

    setTimeout(() => {

      this.highlightId = null;

      this.cdr.detectChanges();

    }, 3000);
  }


  // =========================================================
  // 🔥 POPULAR PANEL (৮ হাজার কার্ড নয় — শুধু Popular + সার্চ রেজাল্ট)
  // =========================================================

  onPopularSearchInput(): void {

    if (this.popularTimer) {
      clearTimeout(this.popularTimer);
    }

    this.popularTimer = setTimeout(() => {

      this.rebuildPopular();

      this.cdr.detectChanges();

    }, 250);
  }

  rebuildPopular(): void {

    const q = this.norm(this.popularSearch);

    if (!q) {

      this.popularVisible =
        this.allCategories.filter(
          (c: any) => c.popular
        );

      return;
    }

    const tokens = q.split(/\s+/).filter(Boolean);

    this.popularVisible =
      this.allCategories
        .filter((c: any) =>
          tokens.every(
            t => (c._key + ' ' + c._pathKey).includes(t)
          )
        )
        .slice(0, 40);
  }

  get filteredPopularCategories() {

    return this.popularVisible;
  }

  // =========================
  // BULK IMPORT (taxonomy Excel)
  // =========================

  onImportFileSelected(event: Event): void {

    const input = event.target as HTMLInputElement;

    this.importFile =
      input.files && input.files.length
        ? input.files[0]
        : null;

    this.importResult = null;
  }

  runCategoryImport(): void {

    if (!this.importFile) {

      alert('আগে একটা .xlsx ফাইল বেছে নাও');
      return;
    }

    if (this.importing) {
      return;
    }

    this.importing = true;
    this.importResult = null;
    this.cdr.detectChanges();

    const formData = new FormData();
    formData.append('file', this.importFile);

    this.http
      .post<any>(
        `${this.api}/import`,
        formData
      )
      .pipe(
        finalize(() => {
          this.importing = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({

        next: (res: any) => {

          this.importResult = {
            mainCreated: res?.mainCreated ?? 0,
            subCreated: res?.subCreated ?? 0,
            childCreated: res?.childCreated ?? 0,
            skippedExisting: res?.skippedExisting ?? 0,
            errors: Array.isArray(res?.errors) ? res.errors : [],
            batchId: res?.batchId ?? null
          };

          this.importFile = null;

          // নতুন category-গুলো tree-তে দেখানোর জন্য রিলোড
          this.load();

          this.loadImportBatches();
        },

        error: (err: any) => {

          console.error('Category import failed', err);

          alert(
            this.getErrorMessage(
              err,
              'Category import failed'
            )
          );
        }

      });
  }


  // =========================
  // 🖼️ BULK IMAGE IMPORT (ফোল্ডার / ছবি / ZIP)
  // =========================

  onImageFilesSelected(event: Event): void {

    const input = event.target as HTMLInputElement;

    const picked = input.files ? Array.from(input.files) : [];

    this.imageFiles = picked.filter(f => {
      const n = f.name.toLowerCase();
      return /\.(jpe?g|jpe|jfif|png|apng|webp|gif|avif|bmp|tiff?|heic|heif|svg|ico|jp2|zip)$/.test(n);
    });

    this.imageResult = null;
    this.imageProgress = '';
  }

  async runImageImport(): Promise<void> {

    if (!this.imageFiles.length) {
      alert('আগে ছবির ফোল্ডার বা ছবি/ZIP বেছে নাও');
      return;
    }

    if (this.imageImporting) {
      return;
    }

    this.imageImporting = true;
    this.imageResult = null;
    this.cdr.detectChanges();

    // ছোট ছোট ব্যাচে পাঠানো (সার্ভার টাইমআউট এড়াতে)
    const MAX_FILES = 10;
    const MAX_BYTES = 6 * 1024 * 1024;

    const batches: File[][] = [];
    let cur: File[] = [];
    let curBytes = 0;

    for (const f of this.imageFiles) {

      const isZip = f.name.toLowerCase().endsWith('.zip');

      if (isZip) {
        if (cur.length) { batches.push(cur); cur = []; curBytes = 0; }
        batches.push([f]);
        continue;
      }

      if (cur.length >= MAX_FILES || (cur.length && curBytes + f.size > MAX_BYTES)) {
        batches.push(cur);
        cur = [];
        curBytes = 0;
      }

      cur.push(f);
      curBytes += f.size;
    }

    if (cur.length) batches.push(cur);

    const total: any = {
      totalImages: 0,
      categoriesUpdated: 0,
      skippedAlreadyHasImage: 0,
      unmatchedFiles: [] as string[],
      ambiguousFiles: [] as string[],
      duplicateFiles: [] as string[],
      failedFiles: [] as string[],
      errorMessages: [] as string[]
    };

    const batchId =
      (typeof crypto !== 'undefined' && (crypto as any).randomUUID)
        ? (crypto as any).randomUUID()
        : 'b' + Date.now() + Math.random().toString(16).slice(2);

    const firstPath: string =
      (this.imageFiles[0] as any)?.webkitRelativePath || '';

    const folderName = firstPath.includes('/') ? firstPath.split('/')[0] : '';

    const label =
      (folderName ? `📁 ${folderName} — ` : '') +
      `${this.imageFiles.length}টা ফাইল`;

    for (let i = 0; i < batches.length; i++) {

      this.imageProgress =
        `ব্যাচ ${i + 1} / ${batches.length} আপলোড হচ্ছে...`;
      this.cdr.detectChanges();

      const fd = new FormData();

      for (const f of batches[i]) {
        fd.append('files', f);
        fd.append('paths', (f as any).webkitRelativePath || f.name);
      }

      fd.append('overwrite', String(this.imageOverwrite));
      fd.append('applyToAllSameName', String(this.imageApplyAllSameName));
      fd.append('batchId', batchId);
      fd.append('label', label);

      try {

        const res: any = await firstValueFrom(
          this.http.post<any>(`${this.api}/image-batches/import`, fd)
        );

        total.totalImages += res?.totalImages ?? 0;
        total.categoriesUpdated += res?.categoriesUpdated ?? 0;
        total.skippedAlreadyHasImage += res?.skippedAlreadyHasImage ?? 0;
        total.unmatchedFiles.push(...(res?.unmatchedFiles ?? []));
        total.ambiguousFiles.push(...(res?.ambiguousFiles ?? []));
        total.duplicateFiles.push(...(res?.duplicateFiles ?? []));
        total.failedFiles.push(...(res?.failedFiles ?? []));
        total.errorMessages.push(...(res?.errorMessages ?? []));

      } catch (err: any) {

        console.error('Category image import failed', err);

        for (const f of batches[i]) {
          total.failedFiles.push((f as any).webkitRelativePath || f.name);
        }

        total.errorMessages.push(
          `ব্যাচ ${i + 1}: ` +
          this.getErrorMessage(err, 'আপলোড ব্যর্থ হয়েছে')
        );
      }
    }

    this.imageResult = total;
    this.imageProgress = '';
    this.imageFiles = [];
    this.imageImporting = false;

    // ছবি বসার পর ট্রি রিলোড
    this.load();
    this.loadImageBatches();
    this.cdr.detectChanges();
  }


  // =========================
  // 🕘 ছবি Import ইতিহাস — Undo / Redo / ছবি বদল
  // =========================

  async loadImageBatches(): Promise<void> {

    try {
      this.imgBatches =
        await firstValueFrom(
          this.http.post<any[]>(`${this.api}/image-batches/list`, {})
        );
    } catch (err) {
      console.error('Image batches load failed', err);
      this.imgBatches = [];
    }

    try {
      this.imgTrash =
        await firstValueFrom(
          this.http.post<any[]>(`${this.api}/image-batches/trash/list`, {})
        );
    } catch (err) {
      console.error('Image trash load failed', err);
      this.imgTrash = [];
    }

    // খোলা ব্যাচ ট্র্যাশে চলে গেলে বন্ধ করে দেওয়া
    if (this.openBatchId &&
        !this.imgBatches.some(x => x.batchId === this.openBatchId)) {
      this.openBatchId = null;
      this.openBatchItems = [];
    }

    this.cdr.detectChanges();
  }

  imgStatusText(b: any): string {

    if (b.status === 'UNDONE') return '↩️ Undo করা (ছবি আগের অবস্থায়)';
    if (b.status === 'PARTIAL') return `🟡 আংশিক — ${b.active}/${b.total}টা বসানো`;
    return `✅ ${b.active}টা ক্যাটাগরিতে বসানো`;
  }

  async toggleBatch(b: any): Promise<void> {

    if (this.openBatchId === b.batchId) {
      this.openBatchId = null;
      this.openBatchItems = [];
      return;
    }

    this.openBatchId = b.batchId;
    this.openBatchItems = [];
    this.openBatchTotal = 0;

    await this.loadBatchItems(false);
  }

  async loadBatchItems(more: boolean): Promise<void> {

    if (!this.openBatchId) return;

    const offset = more ? this.openBatchItems.length : 0;

    try {
      const res: any = await firstValueFrom(
        this.http.post<any>(
          `${this.api}/image-batches/${this.openBatchId}/items?offset=${offset}&limit=40`,
          {}
        )
      );

      this.openBatchTotal = res?.total ?? 0;
      this.openBatchItems = more
        ? [...this.openBatchItems, ...(res?.items ?? [])]
        : (res?.items ?? []);

    } catch (err) {
      this.imgMsg = this.getErrorMessage(err, 'আইটেম লোড করা যায়নি');
    }

    this.cdr.detectChanges();
  }

  private async imgAction(
    key: string,
    url: string,
    body: any,
    okText: (res: any) => string
  ): Promise<void> {

    if (this.imgBusy) return;

    this.imgBusy = key;
    this.imgMsg = '';
    this.cdr.detectChanges();

    try {
      const res: any = await firstValueFrom(this.http.post<any>(url, body));

      this.imgMsg = okText(res);

      await this.loadImageBatches();

      if (this.openBatchId) {
        const keep = Math.max(this.openBatchItems.length, 40);
        const res2: any = await firstValueFrom(
          this.http.post<any>(
            `${this.api}/image-batches/${this.openBatchId}/items?offset=0&limit=${Math.min(keep, 100)}`,
            {}
          )
        );
        this.openBatchTotal = res2?.total ?? 0;
        this.openBatchItems = res2?.items ?? [];
      }

      this.load();

    } catch (err: any) {
      this.imgMsg = this.getErrorMessage(err, 'কাজটা করা যায়নি');
    }

    this.imgBusy = '';
    this.cdr.detectChanges();
  }

  private undoText(res: any): string {
    let t = `↩️ ${res?.done ?? 0}টা ক্যাটাগরির ছবি আগের অবস্থায় ফিরেছে`;
    if (res?.skippedChangedLater) {
      t += ` (${res.skippedChangedLater}টা স্কিপ — পরে হাতে বদলানো হয়েছিল)`;
    }
    return t;
  }

  private redoText(res: any): string {
    let t = `↪️ ${res?.done ?? 0}টা ক্যাটাগরিতে ছবি আবার বসেছে`;
    if (res?.skippedChangedLater) {
      t += ` (${res.skippedChangedLater}টা স্কিপ — এর মধ্যে ছবি বদলানো হয়েছে)`;
    }
    return t;
  }

  undoImageBatch(b: any): void {

    if (!confirm(`এই Import-এর ${b.active}টা ছবি Undo করবে? ক্যাটাগরিগুলো আগের ছবিতে ফিরবে। পরে চাইলে Redo করে ফিরিয়ে আনা যাবে।`)) {
      return;
    }

    this.imgAction(
      'undo:' + b.batchId,
      `${this.api}/image-batches/${b.batchId}/undo`,
      {},
      r => this.undoText(r)
    );
  }

  redoImageBatch(b: any): void {

    this.imgAction(
      'redo:' + b.batchId,
      `${this.api}/image-batches/${b.batchId}/redo`,
      {},
      r => this.redoText(r)
    );
  }

  undoImageItem(it: any): void {

    if (!this.openBatchId) return;

    this.imgAction(
      'u:' + it.categoryId,
      `${this.api}/image-batches/${this.openBatchId}/items/${it.categoryId}/undo`,
      {},
      r => this.undoText(r)
    );
  }

  redoImageItem(it: any): void {

    if (!this.openBatchId) return;

    this.imgAction(
      'r:' + it.categoryId,
      `${this.api}/image-batches/${this.openBatchId}/items/${it.categoryId}/redo`,
      {},
      r => this.redoText(r)
    );
  }

  replaceImageItem(it: any, event: Event): void {

    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];

    if (!file || !this.openBatchId) return;

    const fd = new FormData();
    fd.append('file', file);

    this.imgAction(
      'p:' + it.categoryId,
      `${this.api}/image-batches/${this.openBatchId}/items/${it.categoryId}/replace`,
      fd,
      () => `🔄 "${it.categoryName}"-এর ছবি বদলানো হয়েছে`
    );

    input.value = '';
  }


  // ---------- 🗑️ ছবি Import ট্র্যাশ ----------

  deleteImageBatch(b: any): void {

    if (!confirm('এই Import ডিলিট করবে? ক্যাটাগরিগুলোর ছবি আগের অবস্থায় ফিরবে আর Import-টা ট্র্যাশে যাবে। ট্র্যাশ থেকে চাইলে Restore করা যাবে।')) {
      return;
    }

    this.imgAction(
      'del:' + b.batchId,
      `${this.api}/image-batches/${b.batchId}/delete`,
      {},
      r => `🗑️ Import ট্র্যাশে গেছে — ${r?.done ?? 0}টা ক্যাটাগরির ছবি আগের অবস্থায় ফিরেছে`
    );
  }

  restoreImageBatch(b: any): void {

    this.imgAction(
      'res:' + b.batchId,
      `${this.api}/image-batches/${b.batchId}/restore`,
      {},
      r => `♻️ Import ফিরে এসেছে — ${r?.done ?? 0}টা ক্যাটাগরিতে ছবি আবার বসেছে`
    );
  }

  purgeImageBatch(b: any): void {

    if (!confirm('চিরতরে মুছে ফেলবে? এই Import-এর ইতিহাস আর (অন্য কোথাও ব্যবহার না হওয়া) আপলোড করা ছবিগুলো একেবারে মুছে যাবে। এটা আর ফেরানো যাবে না।')) {
      return;
    }

    this.imgAction(
      'purge:' + b.batchId,
      `${this.api}/image-batches/${b.batchId}/purge`,
      {},
      r => `❌ চিরতরে মুছে গেছে — ইতিহাস ${r?.deletedRecords ?? 0}টা, ছবির ফাইল ${r?.deletedFiles ?? 0}টা`
    );
  }

  emptyImageTrash(): void {

    if (!confirm(`ট্র্যাশের ${this.imgTrash.length}টা Import চিরতরে মুছে ফেলবে? এটা আর ফেরানো যাবে না।`)) {
      return;
    }

    this.imgAction(
      'empty',
      `${this.api}/image-batches/trash/empty`,
      {},
      r => `❌ ট্র্যাশ খালি — ${r?.deletedBatches ?? 0}টা Import, ${r?.deletedFiles ?? 0}টা ছবির ফাইল মুছেছে`
    );
  }


  reset(): void {

    this.editingId = null;


    this.selectedFile = null;

    this.previewImage = null;


    this.form = {

      name: '',

      nameBn: '',

      type: 'MAIN',

      parentId: null
    };


    this.cdr.detectChanges();


    this.load();
  }
}